/**
 * auth.js - Authentication utility functions
 */

// Token storage keys
const TOKEN_KEY = 'rental_management_token';
const USER_KEY = 'rental_management_user';

/**
 * Store authentication data in local storage
 * @param {Object} data - Authentication data from API
 */
const setAuth = (data) => {
  if (data.token) {
    localStorage.setItem(TOKEN_KEY, data.token);
  }
  
  if (data.user) {
    localStorage.setItem(USER_KEY, JSON.stringify(data.user));
  }
};

/**
 * Remove authentication data from local storage
 */
const clearAuth = () => {
  localStorage.removeItem(TOKEN_KEY);
  localStorage.removeItem(USER_KEY);
};

/**
 * Get the current authentication token
 * @returns {string|null} The JWT token or null if not found
 */
const getToken = () => {
  return localStorage.getItem(TOKEN_KEY);
};

/**
 * Get the current authenticated user
 * @returns {Object|null} User data or null if not authenticated
 */
const getUser = () => {
  const userJson = localStorage.getItem(USER_KEY);
  if (!userJson) return null;
  
  try {
    return JSON.parse(userJson);
  } catch (e) {
    clearAuth();
    return null;
  }
};

/**
 * Check if JWT token is expired
 * @param {string} token - The JWT token to check
 * @returns {boolean} True if token is expired, false otherwise
 */
const isTokenExpired = (token) => {
  if (!token) return true;
  
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return true;
    
    const payload = JSON.parse(atob(parts[1]));
    
    if (!payload.exp) return false;
    
    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    return true;
  }
};

/**
 * Check if a user is currently authenticated
 * @returns {boolean} True if authenticated, false otherwise
 */
const isAuthenticated = () => {
  const token = getToken();
  const user = getUser();
  
  if (!token || !user) return false;
  
  if (isTokenExpired(token)) {
    clearAuth();
    return false;
  }
  
  return true;
};

/**
 * Check if current user has a specific role
 * @param {string} roleName - The role to check for (admin, manager, caretaker)
 * @returns {boolean} True if user has the role, false otherwise
 */
const hasRole = (roleName) => {
  const user = getUser();
  if (!user) return false;
  return user.role === roleName;
};

const isAdmin     = () => hasRole('admin');
const isManager   = () => hasRole('manager');
const isCaretaker = () => hasRole('caretaker');

/**
 * Create Auth header with JWT token for API requests
 * @returns {Object} Headers object with Authorization header
 */
const getAuthHeaders = () => {
  const token = getToken();
  
  if (!token || isTokenExpired(token)) {
    clearAuth();
    return {};
  }
  
  return {
    'Authorization': `Bearer ${token}`
  };
};

export default {
  setAuth,
  clearAuth,
  getToken,
  getUser,
  isAuthenticated,
  isTokenExpired,
  hasRole,
  isAdmin,
  isManager,
  isCaretaker,
  getAuthHeaders
};